// src/app/admin/BlogManagement.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Search,
    Edit,
    Trash2,
    Eye,
    EyeOff,
    Calendar,
    Tag,
    ChevronLeft,
    ChevronRight,
    Loader,
    AlertCircle,
    Globe
} from 'lucide-react';
import {BlogService} from "../api/blog.api";
import {designTokens} from "../theme/designTokens";
import {formatBlogDate, getCategoryColor} from "../utils/blogUtils";

export default function BlogManagement() {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [categories, setCategories] = useState([]);
    const [selectedBlogs, setSelectedBlogs] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [ setShowBulkActions] = useState(false);
    const [bulkAction, setBulkAction] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [blogToDelete, setBlogToDelete] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [blogToUpdate, setBlogToUpdate] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [updating, setUpdating] = useState(false);
    const [stats, setStats] = useState({
        total: 0,
        published: 0,
        draft: 0,
        totalViews: 0
    });

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalBlogs: 0,
        limit: 10
    });

    // Load blogs on mount and when filters change
    useEffect(() => {
        loadBlogs();
        loadCategories();
    }, [pagination.currentPage, statusFilter, categoryFilter, searchTerm]);

    const loadBlogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page: pagination.currentPage,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                category: categoryFilter !== 'all' ? categoryFilter : undefined,
                search: searchTerm || undefined,
                sort: '-createdAt'
            };

            const data = await BlogService.getAllAdminBlogs(params);

            // Handle different response structures
            const blogsData = data.blogs || data.data || [];
            setBlogs(blogsData);

            setPagination(data.pagination || {
                currentPage: pagination.currentPage,
                totalPages: data.totalPages || 1,
                totalBlogs: data.total || 0,
                limit: pagination.limit
            });

            // Calculate stats
            const published = blogsData.filter(b => b.status === 'published').length;
            const draft = blogsData.filter(b => b.status === 'draft').length;
            const totalViews = blogsData.reduce((sum, b) => sum + (b.views || 0), 0);

            setStats({
                total: data.total || blogsData.length,
                published,
                draft,
                totalViews
            });

        } catch (error) {
            console.error("Failed to load blogs:", error);
            setError(error.message || "Failed to load blogs");
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const data = await BlogService.getBlogCategories();
            if (data.success && data.data) {
                setCategories(data.data);
            }
        } catch (error) {
            console.error("Failed to load categories:", error);
        }
    };

    // Handle search with debounce
    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Toggle blog status (published/draft)
    const toggleStatus = async (blog) => {
        const newStatus = blog.status === 'published' ? 'draft' : 'published';
        setUpdating(true);
        try {
            await BlogService.updateBlog(blog._id, {
                ...blog,
                status: newStatus,
                publishedAt: newStatus === 'published' ? new Date().toISOString() : blog.publishedAt
            });

            // Update local state
            setBlogs(prev => prev.map(b =>
                b._id === blog._id ? { ...b, status: newStatus } : b
            ));

            // Update stats
            setStats(prev => ({
                ...prev,
                published: newStatus === 'published' ? prev.published + 1 : prev.published - 1,
                draft: newStatus === 'draft' ? prev.draft + 1 : prev.draft - 1
            }));

        } catch (error) {
            console.error("Failed to update status:", error);
            alert("Failed to update blog status");
        } finally {
            setUpdating(false);
            setShowStatusModal(false);
            setBlogToUpdate(null);
        }
    };

    // Delete blog
    const deleteBlog = async (id) => {
        try {
            await BlogService.deleteBlog(id);

            // Update local state
            setBlogs(prev => prev.filter(b => b._id !== id));

            // Update stats
            const deletedBlog = blogs.find(b => b._id === id);
            if (deletedBlog) {
                setStats(prev => ({
                    ...prev,
                    total: prev.total - 1,
                    published: deletedBlog.status === 'published' ? prev.published - 1 : prev.published,
                    draft: deletedBlog.status === 'draft' ? prev.draft - 1 : prev.draft
                }));
            }

            setShowDeleteModal(false);
            setBlogToDelete(null);

        } catch (error) {
            console.error("Failed to delete blog:", error);
            alert("Failed to delete blog");
        }
    };

    // Bulk actions
    const handleBulkAction = async () => {
        if (!bulkAction || selectedBlogs.length === 0) return;

        try {
            if (bulkAction === 'delete') {
                if (!window.confirm(`Are you sure you want to delete ${selectedBlogs.length} blogs?`)) return;

                for (const blogId of selectedBlogs) {
                    await BlogService.deleteBlog(blogId);
                }
            } else if (bulkAction === 'publish') {
                for (const blogId of selectedBlogs) {
                    const blog = blogs.find(b => b._id === blogId);
                    await BlogService.updateBlog(blogId, { ...blog, status: 'published' });
                }
            } else if (bulkAction === 'draft') {
                for (const blogId of selectedBlogs) {
                    const blog = blogs.find(b => b._id === blogId);
                    await BlogService.updateBlog(blogId, { ...blog, status: 'draft' });
                }
            }

            // Reload blogs
            loadBlogs();
            setSelectedBlogs([]);
            setSelectAll(false);
            setShowBulkActions(false);
            setBulkAction('');

        } catch (error) {
            console.error("Bulk action failed:", error);
            alert("Bulk action failed");
        }
    };

    // Handle select all
    useEffect(() => {
        if (selectAll) {
            setSelectedBlogs(blogs.map(b => b._id));
        } else {
            setSelectedBlogs([]);
        }
    }, [selectAll, blogs]);

    // Handle individual selection
    const toggleSelection = (blogId) => {
        setSelectedBlogs(prev => {
            if (prev.includes(blogId)) {
                return prev.filter(id => id !== blogId);
            } else {
                return [...prev, blogId];
            }
        });
    };

    // Status Modal
    const StatusModal = () => (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={modalOverlayStyle}
            onClick={() => setShowStatusModal(false)}
        >
            <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                style={modalContentStyle}
                onClick={(e) => e.stopPropagation()}
            >
                <h3 style={modalTitleStyle}>Change Blog Status</h3>
                <p style={modalTextStyle}>
                    Are you sure you want to change "{blogToUpdate?.title}" status to "{newStatus}"?
                </p>
                <div style={modalActionsStyle}>
                    <button
                        onClick={() => setShowStatusModal(false)}
                        style={modalCancelButtonStyle}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => toggleStatus(blogToUpdate)}
                        disabled={updating}
                        style={modalConfirmButtonStyle}
                    >
                        {updating ? <Loader size={18} className="spin" /> : 'Confirm'}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );

    // Delete Modal
    const DeleteModal = () => (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={modalOverlayStyle}
            onClick={() => setShowDeleteModal(false)}
        >
            <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                style={{ ...modalContentStyle, borderTop: '3px solid #f44336' }}
                onClick={(e) => e.stopPropagation()}
            >
                <AlertCircle size={40} color="#f44336" style={{ marginBottom: '1rem' }} />
                <h3 style={modalTitleStyle}>Delete Blog Post</h3>
                <p style={modalTextStyle}>
                    Are you sure you want to delete "{blogToDelete?.title}"? This action cannot be undone.
                </p>
                <div style={modalActionsStyle}>
                    <button
                        onClick={() => setShowDeleteModal(false)}
                        style={modalCancelButtonStyle}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => deleteBlog(blogToDelete._id)}
                        style={{ ...modalConfirmButtonStyle, background: '#f44336' }}
                    >
                        Delete
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );

    return (
        <div style={pageContainerStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <div>
                    <h1 style={titleStyle}>Blog Management</h1>
                    <p style={subtitleStyle}>
                        Manage your blog posts, publish, draft, and edit content
                    </p>
                </div>
                <button
                    onClick={() => navigate('/admin/blogs/add')}
                    style={addButtonStyle}
                >
                    <Plus size={20} />
                    Add New Blog
                </button>
            </div>

            {/* Stats Cards */}
            <div style={statsContainerStyle}>
                <div style={statCardStyle}>
                    <div style={statIconStyle(designTokens.colors.champagne)}>
                        <Globe size={24} />
                    </div>
                    <div>
                        <div style={statValueStyle}>{stats.total}</div>
                        <div style={statLabelStyle}>Total Blogs</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={statIconStyle('#4CAF50')}>
                        <Eye size={24} />
                    </div>
                    <div>
                        <div style={statValueStyle}>{stats.published}</div>
                        <div style={statLabelStyle}>Published</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={statIconStyle('#FF9800')}>
                        <EyeOff size={24} />
                    </div>
                    <div>
                        <div style={statValueStyle}>{stats.draft}</div>
                        <div style={statLabelStyle}>Drafts</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={statIconStyle('#2196F3')}>
                        <Eye size={24} />
                    </div>
                    <div>
                        <div style={statValueStyle}>{stats.totalViews.toLocaleString()}</div>
                        <div style={statLabelStyle}>Total Views</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div style={filtersContainerStyle}>
                <form onSubmit={handleSearch} style={searchFormStyle}>
                    <Search size={18} color={designTokens.colors.text.tertiary} />
                    <input
                        type="text"
                        placeholder="Search blogs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={searchInputStyle}
                    />
                </form>

                <select
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                    }}
                    style={filterSelectStyle}
                >
                    <option value="all">All Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                </select>

                <select
                    value={categoryFilter}
                    onChange={(e) => {
                        setCategoryFilter(e.target.value);
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                    }}
                    style={filterSelectStyle}
                >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                        <option key={cat._id} value={cat._id}>
                            {cat._id} ({cat.count})
                        </option>
                    ))}
                </select>
            </div>

            {/* Bulk Actions */}
            {selectedBlogs.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={bulkActionsStyle}
                >
                    <span style={{ color: designTokens.colors.text.secondary }}>
                        {selectedBlogs.length} blog{selectedBlogs.length > 1 ? 's' : ''} selected
                    </span>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <select
                            value={bulkAction}
                            onChange={(e) => setBulkAction(e.target.value)}
                            style={bulkSelectStyle}
                        >
                            <option value="">Bulk Actions</option>
                            <option value="publish">Publish</option>
                            <option value="draft">Move to Draft</option>
                            <option value="delete">Delete</option>
                        </select>
                        <button
                            onClick={handleBulkAction}
                            disabled={!bulkAction}
                            style={bulkApplyButtonStyle}
                        >
                            Apply
                        </button>
                        <button
                            onClick={() => {
                                setSelectedBlogs([]);
                                setSelectAll(false);
                            }}
                            style={bulkCancelButtonStyle}
                        >
                            Cancel
                        </button>
                    </div>
                </motion.div>
            )}

            {/* Error Message */}
            {error && (
                <div style={errorContainerStyle}>
                    <AlertCircle size={20} />
                    {error}
                </div>
            )}

            {/* Blogs Table */}
            {loading ? (
                <div style={loadingContainerStyle}>
                    <Loader size={40} className="spin" />
                    <p>Loading blogs...</p>
                </div>
            ) : (
                <>
                    <div style={tableContainerStyle}>
                        <table style={tableStyle}>
                            <thead style={tableHeadStyle}>
                            <tr>
                                <th style={checkboxThStyle}>
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={(e) => setSelectAll(e.target.checked)}
                                        style={checkboxStyle}
                                    />
                                </th>
                                <th style={thStyle}>Title & Slug</th>
                                <th style={thStyle}>Category</th>
                                <th style={thStyle}>Status</th>
                                <th style={thStyle}>Published</th>
                                <th style={thStyle}>Views</th>
                                <th style={thStyle}>Tags</th>
                                <th style={thStyle}>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {blogs.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={emptyStateStyle}>
                                        <p>No blogs found</p>
                                        <button
                                            onClick={() => navigate('/admin/blogs/add')}
                                            style={emptyStateButtonStyle}
                                        >
                                            Create your first blog post
                                        </button>
                                    </td>
                                </tr>
                            ) : (
                                blogs.map(blog => (
                                    <tr key={blog._id} style={tableRowStyle}>
                                        <td style={checkboxTdStyle}>
                                            <input
                                                type="checkbox"
                                                checked={selectedBlogs.includes(blog._id)}
                                                onChange={() => toggleSelection(blog._id)}
                                                style={checkboxStyle}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={titleContainerStyle}>
                                                <h5 style={titleStyle}>{blog.title}</h5>
                                                <small style={slugStyle}>/{blog.slug}</small>
                                            </div>
                                        </td>
                                        <td style={tdStyle}>
                                                <span style={{
                                                    ...categoryBadgeStyle,
                                                    background: `${getCategoryColor(blog.category)}20`,
                                                    color: getCategoryColor(blog.category)
                                                }}>
                                                    {blog.category}
                                                </span>
                                        </td>
                                        <td style={tdStyle}>
                                            <button
                                                onClick={() => {
                                                    setBlogToUpdate(blog);
                                                    setNewStatus(blog.status === 'published' ? 'draft' : 'published');
                                                    setShowStatusModal(true);
                                                }}
                                                style={{
                                                    ...statusBadgeStyle,
                                                    background: blog.status === 'published' ? '#4CAF50' : '#FF9800'
                                                }}
                                                title="Click to toggle status"
                                            >
                                                {blog.status === 'published' ? (
                                                    <><Eye size={14} /> Published</>
                                                ) : (
                                                    <><EyeOff size={14} /> Draft</>
                                                )}
                                            </button>
                                        </td>
                                        <td style={tdStyle}>
                                            {blog.publishedAt ? (
                                                <div style={dateContainerStyle}>
                                                    <Calendar size={14} color={designTokens.colors.text.tertiary} />
                                                    {formatBlogDate(blog.publishedAt)}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={viewsContainerStyle}>
                                                <Eye size={14} color={designTokens.colors.text.tertiary} />
                                                {blog.views?.toLocaleString() || 0}
                                            </div>
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={tagsContainerStyle}>
                                                <Tag size={14} color={designTokens.colors.text.tertiary} />
                                                {blog.tags?.length || 0}
                                            </div>
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={actionsContainerStyle}>
                                                <button
                                                    onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}
                                                    style={actionButtonStyle}
                                                    title="View"
                                                >
                                                    <Eye size={18} />
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/admin/blogs/edit/${blog.slug}`)}
                                                    style={actionButtonStyle}
                                                    title="Edit"
                                                >
                                                    <Edit size={18} />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setBlogToDelete(blog);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    style={{ ...actionButtonStyle, color: '#f44336' }}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div style={paginationContainerStyle}>
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                                disabled={pagination.currentPage === 1}
                                style={paginationButtonStyle}
                            >
                                <ChevronLeft size={18} />
                                Previous
                            </button>

                            <div style={paginationInfoStyle}>
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </div>

                            <button
                                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                                disabled={pagination.currentPage === pagination.totalPages}
                                style={paginationButtonStyle}
                            >
                                Next
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Modals */}
            <AnimatePresence>
                {showStatusModal && <StatusModal />}
                {showDeleteModal && <DeleteModal />}
            </AnimatePresence>

            {/* Global styles for animations */}
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    .spin {
                        animation: spin 1s linear infinite;
                    }
                `}
            </style>
        </div>
    );
}

// ============= Styles =============
const pageContainerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
    background: designTokens.colors.background.primary,
    minHeight: '100vh',
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
};

const titleStyle = {
    fontFamily: designTokens.typography.fontFamilies.display,
    fontSize: '1rem',
    fontWeight: 2200,
    color: designTokens.colors.text.primary,
    margin: 0,
};

const subtitleStyle = {
    color: designTokens.colors.text.secondary,
    marginTop: '0.5rem',
    fontSize: '1rem',
};

const addButtonStyle = {
    padding: '0.75rem 1.5rem',
    background: designTokens.colors.champagne,
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s ease',
};

const statsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
};

const statCardStyle = {
    padding: '1.5rem',
    background: designTokens.colors.background.secondary,
    borderRadius: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
};

const statIconStyle = (color) => ({
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: `${color}20`,
    color: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const statValueStyle = {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: designTokens.colors.text.primary,
};

const statLabelStyle = {
    fontSize: '0.875rem',
    color: designTokens.colors.text.secondary,
};

const filtersContainerStyle = {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap',
};

const searchFormStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    background: designTokens.colors.background.secondary,
    border: `1px solid ${designTokens.colors.border.light}`,
    borderRadius: '0.5rem',
    gap: '0.5rem',
};

const searchInputStyle = {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: '1rem',
    color: designTokens.colors.text.primary,
    outline: 'none',
};

const filterSelectStyle = {
    padding: '0.75rem 2rem 0.75rem 1rem',
    background: designTokens.colors.background.secondary,
    border: `1px solid ${designTokens.colors.border.light}`,
    borderRadius: '0.5rem',
    fontSize: '1rem',
    color: designTokens.colors.text.primary,
    cursor: 'pointer',
    minWidth: '150px',
};

const bulkActionsStyle = {
    padding: '1rem',
    background: designTokens.colors.champagne,
    borderRadius: '0.5rem',
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'white',
};

const bulkSelectStyle = {
    padding: '0.5rem',
    borderRadius: '0.5rem',
    border: 'none',
    fontSize: '0.875rem',
    minWidth: '150px',
};

const bulkApplyButtonStyle = {
    padding: '0.5rem 1rem',
    background: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    color: designTokens.colors.champagne,
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
};

const bulkCancelButtonStyle = {
    padding: '0.5rem 1rem',
    background: 'transparent',
    border: '1px solid white',
    borderRadius: '0.5rem',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer',
};

const errorContainerStyle = {
    padding: '1rem',
    background: '#ffebee',
    color: '#f44336',
    borderRadius: '0.5rem',
    marginBottom: '2rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
};

const loadingContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem',
    gap: '1rem',
    color: designTokens.colors.text.secondary,
};

const tableContainerStyle = {
    background: 'white',
    borderRadius: '1rem',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
};

const tableHeadStyle = {
    background: designTokens.colors.background.secondary,
};

const thStyle = {
    padding: '1rem',
    textAlign: 'left',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: designTokens.colors.text.secondary,
    borderBottom: `2px solid ${designTokens.colors.border.light}`,
};

const checkboxThStyle = {
    ...thStyle,
    width: '40px',
    textAlign: 'center',
};

const checkboxTdStyle = {
    padding: '1rem',
    textAlign: 'center',
};

const checkboxStyle = {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
};

const tdStyle = {
    padding: '1rem',
    borderBottom: `1px solid ${designTokens.colors.border.light}`,
    color: designTokens.colors.text.primary,
};

const tableRowStyle = {
    transition: 'background 0.2s ease',
    '&:hover': {
        background: designTokens.colors.background.secondary,
    },
};

const titleContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
};

// const titleStyle = {
//     fontSize: '1rem',
//     color: designTokens.colors.text.primary,
// };

const slugStyle = {
    fontSize: '0.75rem',
    color: designTokens.colors.text.tertiary,
};

const categoryBadgeStyle = {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500,
    display: 'inline-block',
};

const statusBadgeStyle = {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
};

const dateContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.875rem',
    color: designTokens.colors.text.secondary,
};

const viewsContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.875rem',
    color: designTokens.colors.text.secondary,
};

const tagsContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.875rem',
    color: designTokens.colors.text.secondary,
};

const actionsContainerStyle = {
    display: 'flex',
    gap: '0.5rem',
};

const actionButtonStyle = {
    padding: '0.5rem',
    background: 'transparent',
    border: 'none',
    borderRadius: '0.5rem',
    color: designTokens.colors.text.secondary,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const emptyStateStyle = {
    padding: '4rem',
    textAlign: 'center',
    color: designTokens.colors.text.secondary,
};

const emptyStateButtonStyle = {
    marginTop: '1rem',
    padding: '0.75rem 1.5rem',
    background: designTokens.colors.champagne,
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    cursor: 'pointer',
};

const paginationContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '2rem',
};

const paginationButtonStyle = {
    padding: '0.5rem 1rem',
    background: 'transparent',
    border: `1px solid ${designTokens.colors.border.light}`,
    borderRadius: '0.5rem',
    color: designTokens.colors.text.secondary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
};

const paginationInfoStyle = {
    color: designTokens.colors.text.secondary,
    fontSize: '0.875rem',
};

// Modal Styles
const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
};

const modalContentStyle = {
    background: designTokens.colors.background.primary,
    padding: '2rem',
    borderRadius: '1rem',
    maxWidth: '400px',
    width: '90%',
    textAlign: 'center',
};

const modalTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: designTokens.colors.text.primary,
    marginBottom: '1rem',
};

const modalTextStyle = {
    color: designTokens.colors.text.secondary,
    marginBottom: '2rem',
    lineHeight: 1.6,
};

const modalActionsStyle = {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
};

const modalCancelButtonStyle = {
    padding: '0.75rem 1.5rem',
    background: 'transparent',
    border: `1px solid ${designTokens.colors.border.light}`,
    borderRadius: '0.5rem',
    color: designTokens.colors.text.secondary,
    cursor: 'pointer',
};

const modalConfirmButtonStyle = {
    padding: '0.75rem 1.5rem',
    background: designTokens.colors.champagne,
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
};